import { useState } from "react";

import { useAlertContext, useConfirmationDialogContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { AmbulanzResponse, ambulanzesApi } from "@/api";
import { HANDLE_ERROR_LOCALLY_HEADER } from "@/api/middleware/errorMiddleware";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface Options {
  onActionSuccess?: () => void;
}

export function useAmbulanz(ambulanz: AmbulanzResponse, { onActionSuccess }: Options = {}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const { showYesNoDialog } = useConfirmationDialogContext();
  const { currentUser } = useCurrentUserContext();

  const [editing, setEditing] = useState(false);

  const isCompleted = Boolean(ambulanz.isCompleted);
  const isClaimed = !isCompleted && Boolean(ambulanz.assignedCounselorId);
  const isOpen = !isCompleted && !ambulanz.assignedCounselorId;
  const isOwn = Boolean(ambulanz.assignedCounselorId) && ambulanz.assignedCounselorId === currentUser.counselorId;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QueryKey.Ambulanzes] });

  const { mutate: claim, isPending: isClaiming } = useMutation({
    // Opt out of the global error toast so a 409 shows the descriptive "already claimed" message instead.
    mutationFn: () =>
      ambulanzesApi.claimAmbulanz(
        { ambulanzId: ambulanz.ambulanzId },
        { headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } }
      ),
    onSuccess: () => {
      void invalidate();
      showSuccess(t("ambulanzes.actions.successfullyClaimed"));
      onActionSuccess?.();
    },
    onError: () => {
      // Refresh so the row reflects the colleague's claim.
      void invalidate();
      showError(t("ambulanzes.actions.alreadyClaimed"));
    },
  });

  const { mutate: returnAmbulanz, isPending: isReturning } = useMutation({
    mutationFn: () => ambulanzesApi.returnAmbulanz({ ambulanzId: ambulanz.ambulanzId }),
    onSuccess: () => {
      void invalidate();
      showSuccess(t("ambulanzes.actions.successfullyReturned"));
      onActionSuccess?.();
    },
  });

  const { mutate: completeAmbulanz, isPending: isCompleting } = useMutation({
    mutationFn: () => ambulanzesApi.completeAmbulanz({ ambulanzId: ambulanz.ambulanzId }),
    onSuccess: () => {
      void invalidate();
      showSuccess(t("ambulanzes.actions.successfullyCompleted"));
      onActionSuccess?.();
    },
  });

  const handleComplete = () => {
    showYesNoDialog(
      t("ambulanzes.actions.completeConfirmTitle"),
      t("ambulanzes.actions.completeConfirmText"),
      (confirmed) => {
        if (confirmed) {
          completeAmbulanz();
        }
      }
    );
  };

  return {
    isCompleted,
    isClaimed,
    isOpen,
    isOwn,
    claim,
    returnAmbulanz,
    handleComplete,
    isBusy: isClaiming || isReturning || isCompleting,
    editing,
    setEditing,
  };
}
